import React, { useEffect, useState } from "react";
import PhoneInput from "react-phone-input-2";
import "react-phone-input-2/lib/style.css";
import { useNavigate } from "react-router-dom";
import Toast from "../../../Components/Toast";
import useAuthStore from "../../../store/useAuthStore";
import useEmpIdCheck from "../../../hooks/useEmpIdCheck";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import AllowanceModal from "../Payslip/Allowances/AllowanceModal";
import DeductionModal from "../Payslip/Deductions/DeductionModal";
import { FaPlusCircle } from "react-icons/fa";

const PersonalDetails = ({ formData, handleChange, errors, setFormData }) => {
  const handlePhoneChange = (value, country) => {
    setFormData((prev) => ({
      ...prev,
      phone: `+${country.dialCode}${value.slice(country.dialCode.length)}`,
    }));
  };

  return (
    <div className="w-full md:w-1/2 pr-0 md:pr-4 mb-6 md:mb-0">
      <div className="bg-white p-8 rounded-3xl shadow-sm border border-slate-100 hover:shadow-md transition-all duration-300">
        <h2 className="text-xl font-black mb-8 text-slate-800 border-b pb-4 border-slate-50 flex items-center gap-2">
          <div className="w-2 h-6 bg-indigo-500 rounded-full" />
          Personal Details
        </h2>
        <div className="grid grid-cols-1 gap-5">
          {["name", "email", "gender", "dob", "cid", "tpn", "dateOfJoining"].map((field) => (
            <div key={field}>
              <label className="block text-sm font-medium text-blue-700 mb-2">
                {field === "cid"
                  ? "CID"
                  : field === "tpn"
                    ? "Taxpayer Personal Number (TPN)"
                    : field === "dateOfJoining"
                      ? "Date of Joining"
                      : field.charAt(0).toUpperCase() + field.slice(1)}
                {["name", "email", "gender", "dob", "cid", "department", "salary", "role", "dateOfJoining"].includes(field) && (
                  <span className="text-red-500 ml-1">*</span>
                )}
              </label>

              {field === "gender" ? (
                <select
                  name="gender"
                  value={formData.gender}
                  onChange={handleChange}
                  className={`w-full px-4 py-3 rounded-lg border ${errors.gender ? "border-red-500" : "border-blue-200"
                    } focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none transition-colors bg-white text-gray-800`}
                >
                  <option value="">Select Gender</option>
                  <option value="Male">Male</option>
                  <option value="Female">Female</option>
                </select>
              ) : (
                <input
                  type={
                    field === "dob" || field === "dateOfJoining"
                      ? "date"
                      : field === "cid"
                        ? "number"
                        : "text"
                  }
                  name={field}
                  value={formData[field]}
                  onChange={handleChange}
                  pattern={field === "cid" ? "\\d{11}" : null}
                  className={`w-full px-4 py-3 rounded-lg border ${errors[field] ? "border-red-500" : "border-blue-200"
                    } focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none transition-colors bg-white text-gray-800`}
                  placeholder={
                    field === "cid"
                      ? "11-digit Citizenship ID"
                      : field === "tpn"
                        ? "Enter Taxpayer Personal Number"
                        : `Enter your ${field}`
                  }
                />
              )}

              {errors[field] && (
                <p className="text-red-500 text-sm mt-1">{errors[field]}</p>
              )}
            </div>
          ))}

          <div>
            <label className="block text-sm font-medium text-blue-700 mb-2">
              Phone Number <span className="text-red-500">*</span>
            </label>
            <div className="flex gap-2">
              <div className="relative flex-1">
                <PhoneInput
                  country={"bt"}
                  value={formData.phone}
                  onChange={handlePhoneChange}
                  inputStyle={{
                    width: "100%",
                    paddingLeft: "56px",
                    height: "48px",
                    borderRadius: "0.5rem",
                    borderColor: errors.phone ? "#ef4444" : "#bfdbfe",
                    backgroundColor: "white",
                  }}
                  buttonStyle={{
                    borderColor: errors.phone ? "#ef4444" : "#bfdbfe",
                    borderRadius: "0.5rem 0 0 0.5rem",
                    backgroundColor: "#eff6ff",
                    padding: "0 8px",
                  }}
                  dropdownStyle={{
                    borderRadius: "0.5rem",
                    boxShadow: "0 4px 6px -1px rgba(0, 0, 0, 0.1)",
                  }}
                  enableSearch
                />
              </div>
            </div>
            {errors.phone && (
              <p className="text-red-500 text-sm mt-1">{errors.phone}</p>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

const ProfessionalDetails = ({ formData, handleChange, errors, setFormData, empIdStatus, onRegenerateEmpId }) => {
  const [roles, setRoles] = useState([]);
  const [departments, setDepartments] = useState([]);
  const { token } = useAuthStore();

  useEffect(() => {
    const fetchRoles = async () => {
      try {
        const response = await fetch("/api/getAllRoles", {
          method: "GET",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
        });

        const data = await response.json();
        if (response.ok) {
          setRoles(data.roles);
        } else {
          console.error("Error fetching roles:", data.error);
        }
      } catch (error) {
        console.error("Failed to fetch roles:", error);
      }
    };

    fetchRoles();
  }, [token]);

  useEffect(() => {
    const fetchDepartments = async () => {
      try {
        const response = await fetch(
          "/api/getAllDepartments",
          {
            method: "GET",
            headers: {
              "Content-Type": "application/json",
              Authorization: `Bearer ${token}`,
            },
          }
        );

        const data = await response.json();
        if (response.ok) {
          setDepartments(data.departments);
        } else {
          console.error("Error fetching departments:", data.error);
        }
      } catch (error) {
        console.error("Failed to fetch departments:", error);
      }
    };

    fetchDepartments();
  }, [token]);

  const handleBankAccountChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      bankAccount: {
        ...prev.bankAccount,
        [name]: value
      }
    }));
  };

  return (
    <div className="w-full md:w-1/2 pl-0 md:pl-4">
      <div className="bg-white p-8 rounded-3xl shadow-sm border border-slate-100 hover:shadow-md transition-all duration-300">
        <h2 className="text-xl font-black mb-8 text-slate-800 border-b pb-4 border-slate-50 flex items-center gap-2">
          <div className="w-2 h-6 bg-indigo-500 rounded-full" />
          Professional Details
        </h2>
        <div className="grid grid-cols-1 gap-5">
          {/* Employee ID Field */}
          <div>
            <label className="block text-sm font-medium text-blue-700 mb-2">
              Employee ID <span className="text-red-500">*</span>
            </label>
            <div className="flex gap-2">
              <input
                type="text"
                name="employeeId"
                value={formData.employeeId}
                onChange={handleChange}
                className={`flex-1 px-4 py-3 rounded-lg border focus:ring-2 outline-none transition-colors bg-white text-gray-800 font-mono uppercase ${
                  empIdStatus === "taken"
                    ? "border-red-400 focus:ring-red-300"
                    : empIdStatus === "available"
                    ? "border-green-400 focus:ring-green-300"
                    : "border-blue-200 focus:ring-blue-500"
                }`}
                placeholder="e.g. EMP-A1B2C3"
              />
              <button
                type="button"
                onClick={onRegenerateEmpId}
                className="px-3 py-2 bg-blue-50 text-blue-600 rounded-lg border border-blue-200 hover:bg-blue-100 text-xs font-semibold transition-colors"
                title="Generate new ID"
              >
                Regenerate
              </button>
            </div>
            {/* Status feedback */}
            {empIdStatus === "checking" && (
              <p className="text-xs text-blue-500 mt-1">Checking availability…</p>
            )}
            {empIdStatus === "available" && (
              <p className="text-xs text-green-600 mt-1">✓ Employee ID is available</p>
            )}
            {empIdStatus === "taken" && (
              <p className="text-xs text-red-500 mt-1">✗ Employee ID already exists. Choose a different one.</p>
            )}
            {!empIdStatus && (
              <p className="text-xs text-slate-400 mt-1">Auto-generated. You can edit it to a custom unique ID.</p>
            )}
          </div>

          {["department", "role", formData.role === "Employee" ? "subRole" : null, "minBasicSalary", "salary", "employeeStatus", "grade"].filter(Boolean).map((field) => (
            <div key={field}>
              <label className="block text-sm font-medium text-blue-700 mb-2">
                {field === "salary" ? "Basic Salary" : field === "minBasicSalary" ? "Minimum Basic Salary" : field === "employeeStatus" ? "Employee Status" : field.charAt(0).toUpperCase() + field.slice(1)}
                {["salary"].includes(field) && <span className="text-red-500 ml-1">*</span>}
              </label>

              {field === "role" ? (
                <select
                  name="role"
                  value={formData.role || "Employee"}
                  onChange={handleChange}
                  className={`w-full px-4 py-3 rounded-lg border border-blue-200 focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none transition-colors bg-white text-gray-800`}
                >
                  <option value="Employee">Employee</option>
                  <option value="HR">HR</option>
                  <option value="Accounts">Accountant</option>
                </select>
              ) : field === "subRole" ? (
                <select
                  name="subRole"
                  value={formData.subRole || ""}
                  onChange={handleChange}
                  className={`w-full px-4 py-3 rounded-lg border border-blue-200 focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none transition-colors bg-white text-gray-800`}
                >
                  {roles.length > 0 ? (
                    <>
                      <option value="">Select a Role</option>
                      {roles
                        .filter(r => !["HR", "Accountant", "Accounts"].includes(r.name))
                        .map((role) => (
                          <option key={role._id} value={role.name}>
                            {role.name}
                          </option>
                        ))}
                    </>
                  ) : (
                    <option value="" disabled>
                      No roles available
                    </option>
                  )}
                </select>
              ) : field === "department" ? (
                <select
                  name="department"
                  value={formData.department}
                  onChange={handleChange}
                  className={`w-full px-4 py-3 rounded-lg border ${errors.role ? "border-red-500" : "border-blue-200"
                    } focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none transition-colors bg-white text-gray-800`}
                >
                  {departments.length > 0 ? (
                    <>
                      <option value="">Select a Department</option>
                      {departments.map((department) => (
                        <option
                          key={department._id}
                          value={department.deptName}
                        >
                          {department.deptName}
                        </option>
                      ))}
                    </>
                  ) : (
                    <option value="" disabled>
                      No departments available
                    </option>
                  )}
                </select>
              ) : field === "employeeStatus" ? (
                <select
                  name="employeeStatus"
                  value={formData.employeeStatus || ""}
                  onChange={handleChange}
                  className={`w-full px-4 py-3 rounded-lg border ${errors.employeeStatus ? "border-red-500" : "border-blue-200"
                    } focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none transition-colors bg-white text-gray-800`}
                >
                  <option value="">Select Status</option>
                  <option value="Contract">Contract</option>
                  <option value="Regular">Regular</option>
                  <option value="Wages">Wages</option>
                  <option value="Probation">Probation</option>
                </select>
              ) : (
                <input
                  type={field === "salary" ? "number" : "text"}
                  name={field}
                  value={formData[field]}
                  onChange={handleChange}
                  className={`w-full px-4 py-3 rounded-lg border ${errors[field] ? "border-red-500" : "border-blue-200"
                    } focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none transition-colors bg-white text-gray-800`}
                  placeholder={`Enter ${field === "salary" ? "Basic Salary" : field === "minBasicSalary" ? "Minimum Basic Salary" : field}`}
                  min={field === "salary" || field === "minBasicSalary" ? "0" : undefined}
                />
              )}

              {errors[field] && (
                <p className="text-red-500 text-sm mt-1">{errors[field]}</p>
              )}
            </div>
          ))}

          {/* Bank Account Information Section */}
          <div className="mt-2">
            <h3 className="text-lg font-medium text-blue-700 mb-3">Bank Account Information</h3>

            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-blue-700 mb-2">
                  Bank Name
                </label>
                <input
                  type="text"
                  name="bankName"
                  value={formData.bankAccount?.bankName || ""}
                  onChange={handleBankAccountChange}
                  className="w-full px-4 py-3 rounded-lg border border-blue-200 focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none transition-colors bg-white text-gray-800"
                  placeholder="Enter bank name"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-blue-700 mb-2">
                  Account Number
                </label>
                <input
                  type="text"
                  name="accountNumber"
                  value={formData.bankAccount?.accountNumber || ""}
                  onChange={handleBankAccountChange}
                  className="w-full px-4 py-3 rounded-lg border border-blue-200 focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none transition-colors bg-white text-gray-800"
                  placeholder="Enter account number"
                />
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

const generateEmpId = () =>
  `EMP-${Math.random().toString(36).substr(2, 6).toUpperCase()}`;

const AddNewStaff = () => {
  const navigate = useNavigate();
  const { token } = useAuthStore();
  const [showToast, setShowToast] = useState(false);
  const [error, setError] = useState(null);
  const { empIdStatus, checkEmpId } = useEmpIdCheck(token);
  const [formData, setFormData] = useState({
    employeeId: generateEmpId(),
    name: "",
    email: "",
    gender: "",
    dob: "",
    phone: "",
    cid: "",
    department: "",
    salary: "",
    basicSalary: "",
    minBasicSalary: "",
    role: "Employee",
    subRole: "",
    tpn: "",
    dateOfJoining: "",
    employeeStatus: "",
    grade: "",
    bankAccount: {
      accountNumber: "",
      bankName: ""
    }
  });
  const [errors, setErrors] = useState({});

  const [allowances, setAllowances] = useState([]);
  const [deductions, setDeductions] = useState([]);
  const [showAllowanceModal, setShowAllowanceModal] = useState(false);
  const [showDeductionModal, setShowDeductionModal] = useState(false);
  const [editingAllowance, setEditingAllowance] = useState(null);
  const [editingDeduction, setEditingDeduction] = useState(null);
  const [toast, setToast] = useState(null);

  const fetchAllowances = async () => {
    try {
      const response = await fetch('/api/getAllAllowanceTypes', {
        headers: { Authorization: `Bearer ${token}` },
      });
      const data = await response.json();
      if (response.ok) setAllowances(data.allowanceTypes);
    } catch (error) {
      console.error("Failed to fetch allowances", error);
    }
  };

  const fetchDeductions = async () => {
    try {
      const response = await fetch('/api/getAllDeductionTypes', {
        headers: { Authorization: `Bearer ${token}` },
      });
      const data = await response.json();
      if (response.ok) setDeductions(data.deductionTypes);
    } catch (error) {
      console.error("Failed to fetch deductions", error);
    }
  };

  useEffect(() => {
    if (token) {
      fetchAllowances();
      fetchDeductions();
    }
  }, [token]);

  const handleSaveAllowance = async (allowanceData) => {
    try {
      const url = editingAllowance
        ? `/api/editAllowanceType/${editingAllowance._id}`
        : '/api/addAllowanceType';
      const method = editingAllowance ? 'PUT' : 'POST';

      const response = await fetch(url, {
        method,
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify(allowanceData),
      });

      if (!response.ok) throw new Error('Failed to save allowance');

      setToast({ type: 'success', message: 'Allowance saved successfully' });
      fetchAllowances();
      setShowAllowanceModal(false);
      setEditingAllowance(null);
    } catch (error) {
      setToast({ type: 'error', message: error.message });
    }
  };

  const handleSaveDeduction = async (deductionData) => {
    try {
      const url = editingDeduction
        ? `/api/editDeductionType/${editingDeduction._id}`
        : '/api/addDeductionType';
      const method = editingDeduction ? 'PUT' : 'POST';

      const response = await fetch(url, {
        method,
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify(deductionData),
      });

      if (!response.ok) throw new Error('Failed to save deduction');

      setToast({ type: 'success', message: 'Deduction saved successfully' });
      fetchDeductions();
      setShowDeductionModal(false);
      setEditingDeduction(null);
    } catch (error) {
      setToast({ type: 'error', message: error.message });
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;

    if (name === "cid") {
      if (!/^\d*$/.test(value)) return;
      if (value.length > 11) return;
    }

    setFormData((prev) => ({ ...prev, [name]: value }));

    if (name === "employeeId") {
      checkEmpId(value);
    }
  };

  const validateForm = () => {
    let formErrors = {};
    let isValid = true;

    // Required fields validation
    const requiredFields = [
      "name",
      "email",
      "dob",
      "gender",
      "cid",
      "phone",
      "salary",
      "role",
      "dateOfJoining"
    ];
    requiredFields.forEach((field) => {
      if (!formData[field]) {
        formErrors[field] = `${field.charAt(0).toUpperCase() + field.slice(1)
          } is required`;
        isValid = false;
      }
    });

    // Email validation
    if (formData.email && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      formErrors.email = "Please enter a valid email address";
      isValid = false;
    }

    // CID validation
    if (
      formData.cid &&
      (formData.cid.length !== 11 || !/^\d+$/.test(formData.cid))
    ) {
      formErrors.cid = "CID must be exactly 11 digits";
      isValid = false;
    }

    // Phone validation
    if (formData.phone && !/^\+\d{8,15}$/.test(formData.phone)) {
      formErrors.phone = "Please enter a valid phone number";
      isValid = false;
    }

    setErrors(formErrors);
    return isValid;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (empIdStatus === "taken") {
      setError("Employee ID already exists. Please choose a different one.");
      return;
    }
    if (!validateForm()) return;

    const dataToSend = {
      ...formData,
      employeeId: formData.employeeId.trim().toUpperCase(),
      salary: formData.salary || formData.basicSalary,
      basicSalary: formData.basicSalary || formData.salary,
      minBasicSalary: formData.minBasicSalary
    };
    if (!dataToSend.subRole) {
      delete dataToSend.subRole;
    }

    try {
      const response = await fetch("/api/addEmployee", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify(dataToSend),
      });

      const data = await response.json();
      if (response.ok) {
        setShowToast(true);
        setTimeout(() => {
          setShowToast(false);
          navigate("/staffInfo");
        }, 3000);
      } else {
        setError(data.error || "Error adding employee.");
      }
    } catch (err) {
      console.error(err);
      setError("Failed to save the employee. Please try again.");
    }
  };

  return (
    <div className="w-full max-w-6xl mx-auto mt-10 p-4 md:p-8 bg-[#F8FAFC] rounded-[2.5rem] border border-slate-100 shadow-2xl shadow-slate-200/50">
      <div className="flex items-center mb-10 px-4">
        <button
          onClick={() => navigate("/staffInfo")}
          className="flex items-center text-slate-400 hover:text-indigo-600 font-bold transition-all mr-6 group"
        >
          <ArrowBackIcon className="mr-2 text-sm group-hover:-translate-x-1 transition-transform" /> Back
        </button>
        <h1 className="text-3xl font-black text-slate-900 tracking-tight flex-grow pr-12">Create New <span className="text-indigo-600">Employee</span></h1>
      </div>

      <form onSubmit={handleSubmit} className="flex flex-col md:flex-row gap-6">
        <PersonalDetails
          formData={formData}
          handleChange={handleChange}
          errors={errors}
          setFormData={setFormData}
        />
        <ProfessionalDetails
          formData={formData}
          handleChange={handleChange}
          errors={errors}
          setFormData={setFormData}
          empIdStatus={empIdStatus}
          onRegenerateEmpId={() => {
            const newId = generateEmpId();
            setFormData((prev) => ({ ...prev, employeeId: newId }));
            checkEmpId(newId);
          }}
        />
      </form>

      {/* Allowances & Deductions Section */}
      <div className="mt-8 px-4">
        <div className="bg-white p-8 rounded-3xl shadow-sm border border-slate-100">
          <h2 className="text-xl font-black mb-6 text-slate-800 border-b pb-4 border-slate-50 flex items-center gap-2">
            <div className="w-2 h-6 bg-indigo-500 rounded-full" />
            Allowances & Deductions
          </h2>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            {/* Allowances Sub-section */}
            <div className="bg-slate-50 p-6 rounded-2xl border border-blue-50">
              <div className="flex justify-between items-center mb-4">
                <h3 className="text-xl font-semibold text-blue-900">Allowances</h3>
                <button
                  type="button"
                  onClick={() => {
                    setEditingAllowance(null);
                    setShowAllowanceModal(true);
                  }}
                  className="bg-blue-600 text-white p-2 px-4 rounded-xl flex items-center gap-2 hover:bg-blue-700 transition-all shadow-md active:scale-95"
                >
                  <FaPlusCircle />
                  <span>Manage</span>
                </button>
              </div>
              <div className="space-y-3">
                {allowances
                  .filter(a => a.appliesTo === "All")
                  .map((allowance) => (
                    <div
                      key={allowance._id}
                      className="flex justify-between items-center p-4 bg-white rounded-xl shadow-sm border border-blue-100 hover:shadow-md transition-all cursor-pointer group"
                      onClick={() => {
                        setEditingAllowance(allowance);
                        setShowAllowanceModal(true);
                      }}
                    >
                      <div>
                        <p className="font-bold text-slate-800 group-hover:text-blue-600 transition-colors">
                          {allowance.name}
                        </p>
                        <p className="text-xs font-medium text-slate-500 uppercase tracking-wider">
                          {allowance.calculationType === "Percentage"
                            ? `${allowance.percentage}% of ${allowance.baseSalaryType || "Basic"}`
                            : `Fixed Amount: ${allowance.defaultAmount} Nu.`}
                        </p>
                      </div>
                      <div className="text-right">
                        <p className="font-black text-blue-600 text-lg">
                          {allowance.calculationType === "Amount"
                            ? (allowance.defaultAmount || 0).toLocaleString()
                            : Math.round(
                              (allowance.baseSalaryType === "Minimum Basic Salary"
                                ? (formData.minBasicSalary || 0)
                                : (formData.salary || 0)
                              ) * (allowance.percentage / 100)
                            ).toLocaleString()} Nu.
                        </p>
                      </div>
                    </div>
                  ))}
                {allowances.filter(a => a.appliesTo === "All").length === 0 && (
                  <div className="text-center py-8 bg-white rounded-xl border border-dashed border-slate-200">
                    <p className="text-slate-400">No applicable allowances.</p>
                  </div>
                )}
              </div>
            </div>

            {/* Deductions Sub-section */}
            <div className="bg-slate-50 p-6 rounded-2xl border border-rose-50">
              <div className="flex justify-between items-center mb-4">
                <h3 className="text-xl font-semibold text-rose-900">Deductions</h3>
                <button
                  type="button"
                  onClick={() => {
                    setEditingDeduction(null);
                    setShowDeductionModal(true);
                  }}
                  className="bg-rose-600 text-white p-2 px-4 rounded-xl flex items-center gap-2 hover:bg-rose-700 transition-all shadow-md active:scale-95"
                >
                  <FaPlusCircle />
                  <span>Manage</span>
                </button>
              </div>
              <div className="space-y-3">
                {deductions
                  .filter(d => d.appliesTo === "All")
                  .map((deduction) => (
                    <div
                      key={deduction._id}
                      className="flex justify-between items-center p-4 bg-white rounded-xl shadow-sm border border-rose-100 hover:shadow-md transition-all cursor-pointer group"
                      onClick={() => {
                        setEditingDeduction(deduction);
                        setShowDeductionModal(true);
                      }}
                    >
                      <div>
                        <p className="font-bold text-slate-800 group-hover:text-rose-600 transition-colors">
                          {deduction.name}
                        </p>
                        <p className="text-xs font-medium text-slate-500 uppercase tracking-wider">
                          {deduction.calculationType === "Percentage"
                            ? `${deduction.percentage}% of ${deduction.baseSalaryType || "Basic"}`
                            : `Fixed Amount: ${deduction.defaultAmount} Nu.`}
                        </p>
                      </div>
                      <div className="text-right">
                        <p className="font-black text-rose-600 text-lg">
                          {deduction.calculationType === "Amount"
                            ? (deduction.defaultAmount || 0).toLocaleString()
                            : Math.round(
                              (deduction.baseSalaryType === "Minimum Basic Salary"
                                ? (formData.minBasicSalary || 0)
                                : (formData.salary || 0)
                              ) * (deduction.percentage / 100)
                            ).toLocaleString()} Nu.
                        </p>
                      </div>
                    </div>
                  ))}
                {deductions.filter(d => d.appliesTo === "All").length === 0 && (
                  <div className="text-center py-8 bg-white rounded-xl border border-dashed border-slate-200">
                    <p className="text-slate-400">No applicable deductions.</p>
                  </div>
                )}
              </div>
            </div>
          </div>
          <p className="mt-6 text-slate-400 text-xs text-center border-t pt-4">Specific adjustments can be made after registration in the Staff Details page.</p>
        </div>
      </div>

      <div className="flex flex-col md:flex-row justify-end gap-4 mt-12 px-4 pb-4">
        <button
          type="button"
          className="px-10 py-4 text-slate-400 font-bold hover:text-rose-500 transition-all"
          onClick={() => navigate("/staffInfo")}
        >
          Discard
        </button>
        <button
          type="submit"
          className="px-12 py-4 bg-slate-900 text-white rounded-2xl hover:bg-indigo-600 transition-all font-black shadow-xl shadow-slate-200"
          onClick={handleSubmit}
        >
          Save & Register
        </button>
      </div>

      {showToast && (
        <Toast
          type="success"
          title="Employee Added Successfully!"
          message="The employee was added to the system."
          duration={3000}
          onClose={() => setShowToast(false)}
        />
      )}
      {error && (
        <Toast type="error" message={error} onClose={() => setError(null)} />
      )}
      {toast && (
        <Toast
          type={toast.type}
          message={toast.message}
          onClose={() => setToast(null)}
        />
      )}

      {showAllowanceModal && (
        <AllowanceModal
          isOpen={showAllowanceModal}
          onClose={() => {
            setShowAllowanceModal(false);
            setEditingAllowance(null);
          }}
          onSave={handleSaveAllowance}
          editingAllowance={editingAllowance}
        />
      )}

      {showDeductionModal && (
        <DeductionModal
          isOpen={showDeductionModal}
          onClose={() => {
            setShowDeductionModal(false);
            setEditingDeduction(null);
          }}
          onSave={handleSaveDeduction}
          editingDeduction={editingDeduction}
        />
      )}
    </div>
  );
};

export default AddNewStaff;
